import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from "lucide-react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { BUYER_OPTIONS } from "../funnel/BuyerProfile";
import { useFunnelStages, usePermissions } from "../funnel/hooks";
import { FunnelShell, NativeSelect } from "../funnel/shared";
import type { Script, ScriptObjection, ScriptStep } from "./types";

/**
 * Call scripts by funnel stage: ordered steps and answers to objections.
 * Everyone reads them (they show beside the call on the lead card);
 * people with the settings permission edit them.
 */
export const ScriptsPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const canEdit = usePermissions().includes("settings");
  const stages = useFunnelStages();
  const { data: scripts = [], refetch } = useGetList<Script>("scripts", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" },
  });
  const [selectedId, setSelectedId] = useState<Identifier>();
  const selected = scripts.find((s) => s.id === selectedId) ?? scripts[0];
  const [draft, setDraft] = useState<Script | null>(null);
  useEffect(
    () => setDraft(selected ? structuredClone(selected) : null),
    [selected],
  );
  const stageLabel = (value: string | null) =>
    value == null
      ? translate("crm.scripts.any_stage")
      : (stages.find((s) => s.value === value)?.label ?? value);

  const create = async () => {
    const { data } = await dataProvider.create<Script>("scripts", {
      data: {
        title: translate("crm.scripts.new_title"),
        stage: null,
        steps: [{ title: translate("crm.scripts.step_title"), text: "" }],
        objections: [],
        active: true,
      },
    });
    await refetch();
    setSelectedId(data.id);
  };

  const save = async () => {
    if (!draft || !selected) return;
    try {
      await dataProvider.update("scripts", {
        id: draft.id,
        data: {
          title: draft.title,
          stage: draft.stage,
          steps: draft.steps,
          objections: draft.objections,
          active: draft.active,
          updated_at: new Date().toISOString(),
        },
        previousData: selected,
      });
      notify("crm.scripts.saved");
      refetch();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  const remove = async () => {
    if (!selected || !window.confirm(translate("crm.scripts.confirm_delete")))
      return;
    await dataProvider.delete("scripts", {
      id: selected.id,
      previousData: selected,
    });
    setSelectedId(undefined);
    refetch();
  };

  const setStep = (index: number, change: Partial<ScriptStep>) =>
    draft &&
    setDraft({
      ...draft,
      steps: draft.steps.map((s, i) => (i === index ? { ...s, ...change } : s)),
    });
  const moveStep = (index: number, delta: number) => {
    if (!draft) return;
    const steps = [...draft.steps];
    const [step] = steps.splice(index, 1);
    steps.splice(index + delta, 0, step);
    setDraft({ ...draft, steps });
  };
  const setObjection = (index: number, change: Partial<ScriptObjection>) =>
    draft &&
    setDraft({
      ...draft,
      objections: draft.objections.map((o, i) =>
        i === index ? { ...o, ...change } : o,
      ),
    });
  const missingObjections = BUYER_OPTIONS.objections.filter(
    (key) => !draft?.objections.some((o) => o.key === key),
  );

  return (
    <FunnelShell
      title={translate("crm.scripts.title")}
      actions={
        canEdit ? (
          <Button type="button" className="ml-auto" onClick={create}>
            <Plus className="mr-1 size-4" />
            {translate("crm.scripts.new")}
          </Button>
        ) : undefined
      }
    >
      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        <ul className="flex flex-col gap-2">
          {scripts.map((script) => (
            <li key={script.id}>
              <button
                type="button"
                onClick={() => setSelectedId(script.id)}
                className={cn(
                  "w-full rounded-xl border p-3 text-left",
                  script.id === selected?.id
                    ? "border-primary bg-accent/60"
                    : "bg-card",
                  !script.active && "opacity-60",
                )}
              >
                <div className="font-semibold">{script.title}</div>
                <div className="text-xs text-muted-foreground">
                  {stageLabel(script.stage)} ·{" "}
                  {translate("crm.scripts.steps_count", {
                    smart_count: script.steps.length,
                  })}
                </div>
              </button>
            </li>
          ))}
          {scripts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.scripts.empty")}
            </p>
          ) : null}
        </ul>

        {draft ? (
          <div className="flex min-w-0 flex-col gap-5 rounded-2xl border bg-card p-5">
            <div className="grid gap-3 sm:grid-cols-[1fr_220px_auto] sm:items-end">
              <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
                {translate("crm.scripts.name")}
                <Input
                  value={draft.title}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setDraft({ ...draft, title: e.target.value })
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
                {translate("crm.scripts.stage")}
                <NativeSelect
                  value={draft.stage ?? ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setDraft({ ...draft, stage: e.target.value || null })
                  }
                >
                  <option value="">{translate("crm.scripts.any_stage")}</option>
                  {stages.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </label>
              <label className="flex items-center gap-2 pb-2 text-sm">
                <Switch
                  checked={draft.active}
                  disabled={!canEdit}
                  onCheckedChange={(active) => setDraft({ ...draft, active })}
                />
                {translate("crm.scripts.active")}
              </label>
            </div>

            <section className="flex flex-col gap-3">
              <h2 className="font-bold">{translate("crm.scripts.steps")}</h2>
              <p className="text-xs text-muted-foreground">
                {translate("crm.scripts.name_hint")}
              </p>
              {draft.steps.map((step, index) => (
                <div key={index} className="flex gap-3 rounded-xl border p-3">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-bold">
                    {index + 1}
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Input
                      value={step.title}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setStep(index, { title: e.target.value })
                      }
                    />
                    <Textarea
                      rows={2}
                      value={step.text}
                      disabled={!canEdit}
                      onChange={(e) => setStep(index, { text: e.target.value })}
                    />
                  </div>
                  {canEdit ? (
                    <div className="flex flex-col gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === 0}
                        onClick={() => moveStep(index, -1)}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={index === draft.steps.length - 1}
                        onClick={() => moveStep(index, 1)}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setDraft({
                            ...draft,
                            steps: draft.steps.filter((_, i) => i !== index),
                          })
                        }
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </div>
                  ) : null}
                </div>
              ))}
              {canEdit ? (
                <Button
                  type="button"
                  variant="outline"
                  className="self-start"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      steps: [...draft.steps, { title: "", text: "" }],
                    })
                  }
                >
                  <Plus className="mr-1 size-4" />
                  {translate("crm.scripts.add_step")}
                </Button>
              ) : null}
            </section>

            <section className="flex flex-col gap-3">
              <h2 className="font-bold">
                {translate("crm.scripts.objections")}
              </h2>
              {draft.objections.map((objection, index) => (
                <div key={index} className="flex gap-3 rounded-xl border p-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Input
                      value={objection.title}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setObjection(index, { title: e.target.value })
                      }
                    />
                    <Textarea
                      rows={2}
                      value={objection.answer}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setObjection(index, { answer: e.target.value })
                      }
                    />
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          objections: draft.objections.filter(
                            (_, i) => i !== index,
                          ),
                        })
                      }
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  ) : null}
                </div>
              ))}
              {canEdit ? (
                <div className="flex flex-wrap gap-1.5">
                  {missingObjections.map((key) => (
                    <Button
                      key={key}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          objections: [
                            ...draft.objections,
                            {
                              key,
                              title: translate(`crm.buyer.objections.${key}`),
                              answer: "",
                            },
                          ],
                        })
                      }
                    >
                      <Plus className="mr-1 size-3" />
                      {translate(`crm.buyer.objections.${key}`)}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        objections: [
                          ...draft.objections,
                          {
                            key: `custom_${Date.now()}`,
                            title: "",
                            answer: "",
                          },
                        ],
                      })
                    }
                  >
                    <Plus className="mr-1 size-3" />
                    {translate("crm.scripts.custom_objection")}
                  </Button>
                </div>
              ) : null}
            </section>

            <section className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
              <div className="font-semibold text-foreground">
                {translate("crm.scripts.ai_title")}
              </div>
              {translate("crm.scripts.ai_hint")}
            </section>

            {canEdit ? (
              <div className="flex justify-between">
                <Button type="button" variant="ghost" onClick={remove}>
                  <Trash2 className="mr-1 size-4" />
                  {translate("ra.action.delete")}
                </Button>
                <Button type="button" onClick={save}>
                  <Save className="mr-1 size-4" />
                  {translate("ra.action.save")}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </FunnelShell>
  );
};

ScriptsPage.path = "/scripts";
